import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, LayoutDashboard, LogOut, Settings as SettingsIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios, { API } from "@/lib/apiClient";

const PULSE_COPY = {
  healthy: { color: "#22C55E", text: "Infrastructure: Verified" },
  degraded: { color: "#F59E0B", text: "Infrastructure: Re-verifying" },
  failed: { color: "#F97316", text: "Infrastructure: Verification In Progress" },
  pending: { color: "#64748B", text: "Infrastructure: Pending Pulse" },
};

const InfrastructurePulseBadge = () => {
  const [overallStatus, setOverallStatus] = useState("pending");

  useEffect(() => {
    let cancelled = false;
    const fetchPulse = () => {
      axios
        .get(`${API}/system/nodes`)
        .then((res) => {
          if (!cancelled) setOverallStatus(res.data.overall_status || "pending");
        })
        .catch(() => {});
    };
    fetchPulse();
    const interval = setInterval(fetchPulse, 45000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const copy = PULSE_COPY[overallStatus] || PULSE_COPY.pending;

  return (
    <div
      className="hidden items-center gap-2 rounded-full border border-surface-border bg-obsidian/60 px-3 py-1.5 sm:flex"
      title={copy.text}
      data-testid="infrastructure-pulse-badge"
    >
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: copy.color, boxShadow: `0 0 6px ${copy.color}` }} data-testid="infrastructure-pulse-dot" />
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-testid="infrastructure-pulse-label">{copy.text}</span>
    </div>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-surface-border bg-obsidian/90 backdrop-blur-xl" data-testid="navbar">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight text-white" data-testid="navbar-logo">
          <Radar className="h-5 w-5 text-sovereign" />
          BRAND<span className="text-sovereign">SIGNAL</span>
        </Link>
        <div className="flex items-center gap-3">
          <InfrastructurePulseBadge />
          {user ? (
            <>
              <Link to="/dashboard" data-testid="navbar-dashboard-link">
                <Button variant="ghost" size="sm" className="gap-2 text-slate-300 hover:text-white">
                  <LayoutDashboard className="h-4 w-4" /> Dashboard
                </Button>
              </Link>
              <Link to="/settings" data-testid="navbar-settings-link">
                <Button variant="ghost" size="sm" className="gap-2 text-slate-300 hover:text-white">
                  <SettingsIcon className="h-4 w-4" /> Settings
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2 border-surface-border" data-testid="navbar-logout-btn">
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </>
          ) : user === false ? (
            <>
              <Link to="/login" data-testid="navbar-login-link">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">Log in</Button>
              </Link>
              <Link to="/register" data-testid="navbar-register-link">
                <Button size="sm" className="bg-sovereign text-white hover:bg-sovereign/90">Sign up</Button>
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </nav>
  );
};
